import { motion } from "framer-motion";
import { Check, ArrowRight, Home, Building2 } from "lucide-react";
import { IMAGES, COMMERCIAL } from "../../data/site";
import { Reveal, Kicker } from "./Reveal";
import { useEstimate } from "./EstimateDialog";

const RESIDENTIAL = [
  "Interior & exterior painting",
  "Flooring — LVP, laminate & more",
  "Kitchen & bathroom upgrades",
  "Drywall repair & installation",
  "Trim, doors & finish carpentry",
  "TV mounting, fans & lighting",
  "Furniture & outdoor assembly",
  "The whole punch list, handled",
];

export const Residential = () => {
  const { open } = useEstimate();
  return (
    <section id="residential" className="bg-white py-24 lg:py-32" data-testid="residential">
      <div className="mx-auto grid max-w-[1400px] items-center gap-14 px-5 lg:grid-cols-2 lg:px-10">
        <Reveal>
          <div className="hover-zoom clip-frame overflow-hidden rounded-2xl">
            <img src={IMAGES.residential} alt="Luxury residential interior in Charlotte" className="aspect-[4/3] w-full object-cover" />
          </div>
        </Reveal>
        <div>
          <Reveal><Kicker><Home className="h-3.5 w-3.5" /> Residential</Kicker></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display mt-4 text-4xl font-extrabold sm:text-5xl">
              Your home, elevated by real craftsmanship.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-ffc-gray">
              From quick fixes to full refreshes, we treat your home like our own — clean job sites,
              honest pricing and finishes you&apos;ll be proud of for years.
            </p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            {RESIDENTIAL.map((r, i) => (
              <motion.div key={r} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 text-sm font-medium">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ffc-blue/10 text-ffc-blue">
                  <Check className="h-3.5 w-3.5" />
                </span>
                {r}
              </motion.div>
            ))}
          </div>
          <button onClick={open} data-testid="residential-cta"
            className="btn-red mt-9 inline-flex items-center gap-2 rounded-full px-7 py-4 font-semibold hover:bg-ffc-red">
            Get Free Estimate <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export const Commercial = () => {
  const { open } = useEstimate();
  return (
    <section id="commercial" className="relative overflow-hidden bg-ffc-ink py-24 text-white lg:py-32 grain" data-testid="commercial">
      <div className="mx-auto grid max-w-[1400px] items-center gap-14 px-5 lg:grid-cols-2 lg:px-10">
        <div className="order-2 lg:order-1">
          <Reveal><span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-ffc-red"><Building2 className="h-3.5 w-3.5" /> Commercial</span></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display mt-4 text-4xl font-extrabold sm:text-5xl">
              Facilities that stay sharp, without the downtime.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-white/70">
              Offices, retail and managed properties trust Fast Fix for dependable maintenance and
              professional installations — scheduled around your business, not the other way around.
            </p>
          </Reveal>
          <div className="mt-8 flex flex-wrap gap-3">
            {COMMERCIAL.map((c, i) => (
              <motion.span key={c} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-white/90">
                {c}
              </motion.span>
            ))}
          </div>
          <button onClick={open} data-testid="commercial-cta"
            className="btn-red mt-9 inline-flex items-center gap-2 rounded-full px-7 py-4 font-semibold hover:bg-ffc-red">
            Discuss Your Facility <ArrowRight className="h-5 w-5" />
          </button>
        </div>
        <Reveal delay={0.1}>
          <div className="order-1 hover-zoom clip-frame overflow-hidden rounded-2xl lg:order-2">
            <img src={IMAGES.commercial} alt="Modern commercial office in Charlotte" className="aspect-[4/3] w-full object-cover" />
          </div>
        </Reveal>
      </div>
    </section>
  );
};
